<script lang="ts">
  // The repository page's Branches tab (docs/design.md "Repository page"): the default branch, the viewer's
  // branches without a pull request, those with one (shown as it, with whose turn the dashboard says it is), then
  // others' and stale ones, collapsed. Each row offers its compare (Review), its history, and GitHub's page that
  // opens a pull request; its details and reviewed share are read once needed. The forms start any compare or
  // history by name.
  import { onMount } from 'svelte';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import LoadError from '../lib/components/LoadError.svelte';
  import { failureOf } from '../lib/api/failure';
  import { oneStep } from '../lib/loading/steps';
  import { holdOrder, openPullUrl, reviewState, turnText, type Branch, type BranchGroup, type HeldRows } from '../lib/logic/branches';
  import type { ReviewedShare } from '../lib/logic/branchReviewed';
  import { involvement, involvementText, involvementTitle } from '../lib/logic/involvement';
  import { turn } from '../lib/logic/stackRail';
  import { relTime } from '../lib/logic/time';
  import { comparePath, historyPath, prPath } from '../lib/router';
  import { nav } from '../lib/nav.svelte';
  import { session } from '../lib/state/session.svelte';
  import { BranchesStore, type BranchesWorld } from '../lib/state/branches.svelte';

  interface Props {
    owner: string;
    repo: string;
    /** What the store reads (tests); GitHub by default. */
    world?: BranchesWorld;
    /** Whose branches are "yours" (tests); the signed-in user by default. */
    viewer?: string;
  }
  let { owner, repo, world, viewer }: Props = $props();

  // svelte-ignore state_referenced_locally
  const store = new BranchesStore(owner, repo, { world, viewer: viewer === undefined ? undefined : () => viewer });
  onMount(() => {
    void store.open();
    return () => store.dispose();
  });

  const webUrl = $derived(session.config?.github_web_url ?? 'https://github.com');
  const def = $derived(store.defaultBranch ?? 'main');
  /** A default branch to measure from and compare with: GitHub's names one that exists. */
  const hasBase = $derived(store.status === 'ready');
  const me = $derived(viewer ?? session.user?.login ?? '');

  // While the viewer is on a row, the rows keep the groups and order they had: more of the listing arriving (sorted
  // in newest first) would shift the row under them, and its details moving it to another group would re-create it
  // there and drop the focus. They take their places once focus leaves the list.
  let held: HeldRows | null = $state(null);
  const groups = $derived(holdOrder(store.groups, held));
  function onfocusin() {
    held ??= store.groups.map((g) => ({ group: g.group, names: g.branches.map((b) => b.name) }));
  }
  function onfocusout(e: FocusEvent) {
    if (!(e.currentTarget as Element).contains(e.relatedTarget as Node | null)) held = null;
  }

  const TITLES: Record<BranchGroup, string> = {
    default: 'Default branch',
    yours: 'Yours without a pull request',
    pulls: 'With a pull request',
    others: "Other people's branches",
    stale: 'Stale',
  };
  const FOLDED: ReadonlySet<BranchGroup> = new Set(['others', 'stale']);

  const DOTS = { success: 'good', failure: 'bad', pending: 'pending', none: '' } as const;
  const CHECKS = { success: 'checks passed', failure: 'checks failing', pending: 'checks running', none: 'no checks' } as const;

  function standing(b: Branch): string | null {
    const d = b.detail;
    if (!d || d.aheadBy === null || d.behindBy === null) return null;
    return `${d.aheadBy} ahead, ${d.behindBy} behind`;
  }

  function shareText(s: ReviewedShare | null): { text: string; title?: string } {
    if (!s || !s.known) return { text: '… reviewed' };
    if (!s.total) return { text: 'nothing to review' };
    const pct = `${Math.round((s.done / s.total) * 100)}%`;
    // GitHub lists 300 files and says nothing of the rest: the figure is of those, not a bound on the whole.
    if (s.firstFiles) return { text: `${pct} of the first 300 files reviewed`, title: `${s.done} of ${s.total} changed lines in the first 300 files GitHub lists are reviewed; the files past them are not counted` };
    return { text: `${pct} reviewed`, title: `${s.done} of ${s.total} changed lines reviewed` };
  }

  /** Reads `b`'s details and reviewed share once its row is on screen (at once where the browser cannot tell). */
  function onScreen(node: Element, b: Branch) {
    let current = b;
    if (typeof IntersectionObserver === 'undefined') {
      store.see(current);
      return { update: (next: Branch) => store.see((current = next)) };
    }
    let visible = false;
    const io = new IntersectionObserver((records) => {
      visible = records.some((r) => r.isIntersecting);
      if (visible) store.see(current);
    });
    io.observe(node);
    return {
      // Its details arrived while it is on screen: its share is read next.
      update: (next: Branch) => {
        current = next;
        if (visible) store.see(current);
      },
      destroy: () => io.disconnect(),
    };
  }

  // Compare a branch: review a pushed branch before (or beside) its pull request.
  let base = $state('');
  let head = $state('');
  let compareError = $state('');
  function startCompare(e: SubmitEvent) {
    e.preventDefault();
    const path = comparePath(owner, repo, base, head);
    if (!path) {
      compareError = 'Name a branch, tag or commit to compare (relative revisions such as main~2 are not taken).';
      return;
    }
    compareError = '';
    nav.go(path);
  }

  // Read a branch's history (or a tag's, a commit's, or a path's in one): the page finds where the ref ends.
  let spec = $state('');
  let historyError = $state('');
  function startHistory(e: SubmitEvent) {
    e.preventDefault();
    const path = historyPath(owner, repo, spec);
    if (!path) {
      historyError = 'Name a branch, tag or commit, then a path in it if you like (relative revisions such as main~2 are not taken).';
      return;
    }
    historyError = '';
    nav.go(path);
  }
</script>

{#snippet row(b: Branch, group: BranchGroup)}
  {@const d = b.detail}
  {@const ab = standing(b)}
  {@const history = historyPath(owner, repo, b.name)}
  <!-- Something to review and to open a pull request of: commits ahead, and not merged already (a squash or rebase merge leaves them ahead). -->
  {@const ahead = d?.aheadBy !== 0 && !d?.merged}
  <li class="pr-row branch-row" data-branch={b.name} use:onScreen={b}>
    <span class="dot {d ? DOTS[d.checks] : ''}" title={d ? CHECKS[d.checks] : undefined}></span>
    <div class="pr-row-main">
      {#if d?.pull}
        {@const pa = store.attention.get(d.pull.id)}
        {@const t = pa ? turnText(turn(pa.members, d.pull.author, me)) : null}
        {@const inv = pa ? involvement(pa.pr, me) : null}
        <a class="pr-row-title" href={prPath(owner, repo, d.pull.number)}>#{d.pull.number} {d.pull.title}</a>
        <div class="pr-row-sub">
          <span class="mono">{b.name}</span>
          {#if d.pull.baseRef && d.pull.baseRef !== def}<span>into <span class="mono">{d.pull.baseRef}</span></span>{/if}
          <span class="pill neutral">{reviewState(d.pull)}</span>
          {#if inv}<span class="pill neutral" title={involvementTitle(inv)}>{involvementText(inv)}</span>{/if}
          {#if t === 'your turn'}<b class="me">{t}</b>{:else if t}<span>{t}</span>{/if}
        </div>
      {:else}
        <a class="pr-row-title mono" href={group === 'default' || !hasBase ? history : (comparePath(owner, repo, def, b.name) ?? history)}>{b.name}</a>
        <div class="pr-row-sub"><span>{d ? d.headline : '…'}</span></div>
      {/if}
      <div class="pr-row-sub">
        {#if ab && group !== 'default'}<span class="ahead-behind">{ab}</span>{/if}
        <span>pushed {relTime(b.tip.committedDate)}{#if b.tip.author}{' '}by {b.tip.author}{/if}</span>
        {#if d}<span class="sr-only">{CHECKS[d.checks]}</span>{/if}
        {#if store.detailFailed(b)}
          <button class="link-button" onclick={() => store.retryDetails()}>Couldn't read this branch's details · Try again</button>
        {:else if d?.merged && !d.pull}
          <span class="reviewed">merged</span>
        {:else if group !== 'default' && hasBase && !store.unrecorded && d}
          {#if store.shareFailed(b)}
            <button class="link-button reviewed" onclick={() => store.retryShare(b)}>Couldn't read how much is reviewed · Try again</button>
          {:else}
            {@const share = store.share(b)}
            {@const s = shareText(share)}
            {#if share?.known && share.lowerBound}
              <!-- Marks on an earlier blob that only the compare page's line maps carry: at least this, and the compare recounts. -->
              <a class="reviewed" href={d.pull ? prPath(owner, repo, d.pull.number, 'files') : comparePath(owner, repo, def, b.name)} title="Some of your marks are on an earlier version of a file; the compare page carries them to this one and counts them"
                >≥ {s.text} · open the compare to recount</a
              >
            {:else if share?.theseVersions}
              <!-- Its earlier versions were not looked up (the tab's budget, or GitHub's hourly one, ran low). -->
              <span class="reviewed" title={`${s.title ?? s.text}: counted from marks on these versions; open the compare to count marks on earlier ones`}
                >{s.text}{' '}<span class="muted">(counted from marks on these versions)</span></span
              >
            {:else}
              <span class="reviewed" title={s.title}>{s.text}</span>
            {/if}
          {/if}
        {/if}
      </div>
    </div>
    <div class="branch-actions">
      {#if group !== 'default' && (d?.pull || (hasBase && ahead))}
        <a class="btn small" href={d?.pull ? prPath(owner, repo, d.pull.number) : comparePath(owner, repo, def, b.name)}>Review</a>
      {/if}
      {#if history}<a class="btn small" href={history}>History</a>{/if}
      {#if group !== 'default' && hasBase && !d?.pull && ahead}
        <a class="btn small" href={openPullUrl(webUrl, owner, repo, def, b.name)} target="_blank" rel="noreferrer" data-external>Open pull request ↗</a>
      {/if}
    </div>
  </li>
{/snippet}

<form class="compare-start" aria-label="Compare a branch" onsubmit={startCompare}>
  <b>Compare a branch</b>
  <label>Base <input bind:value={base} placeholder="default branch" spellcheck="false" autocomplete="off" /></label>
  <span aria-hidden="true">←</span>
  <label>Head <input bind:value={head} placeholder="feat/x or ana:feat" spellcheck="false" autocomplete="off" required aria-describedby={compareError ? 'compare-start-error' : undefined} /></label>
  <button class="btn small" type="submit">Compare</button>
  {#if compareError}<p class="compare-start-error small" id="compare-start-error" role="alert">{compareError}</p>{/if}
</form>
<form class="compare-start" aria-label="Read a branch's history" onsubmit={startHistory}>
  <b>Read a history</b>
  <label>Branch <input bind:value={spec} placeholder="default branch, or feat/x/src" spellcheck="false" autocomplete="off" aria-describedby={historyError ? 'history-start-error' : undefined} /></label>
  <button class="btn small" type="submit">Commits</button>
  {#if historyError}<p class="compare-start-error small" id="history-start-error" role="alert">{historyError}</p>{/if}
</form>
<LoadingGate
  phase={store.status === 'loading' ? 'loading' : store.status === 'error' && store.failure ? 'failed' : 'ready'}
  view={oneStep(`${owner}/${repo}`, 'Branches', store.status === 'error' && store.failure ? { failure: store.failure, what: `the branches of ${owner}/${repo}` } : 'loading')}
  onretry={() => store.load()}
>
{#if store.status === 'notfound'}
  <div class="center-msg"><h1>Repository not found</h1><p class="muted">{store.notFound}</p></div>
{:else if store.status === 'empty'}
  <div class="center-msg"><h1>No branches yet</h1><p class="muted">{owner}/{repo} has no branches: nothing has been pushed to it.</p></div>
{:else}
  {#if store.status === 'nodefault'}
    <p class="banner branches-note" role="note">
      GitHub names <span class="mono">{def}</span> as {owner}/{repo}'s default branch, but it does not exist, so there is nothing to compare
      these branches with or measure them from. Their histories are below.
    </p>
  {/if}
  {#if store.unrecorded}
    <p class="muted empty-line" role="note">Changeling keeps no review marks for {owner}/{repo} (the App is not installed on it), so how much of each branch you reviewed is not shown.</p>
  {:else if store.marks.error}
    <LoadError failure={failureOf(new Error(store.marks.error))} what="your review marks" onretry={() => store.marks.load()} />
  {/if}
  {#if store.listing}
    <p class="muted branches-note" role="status">Listing branches: {store.branches.length} of {store.total} read so far.</p>
  {/if}
  <div class="branch-groups" onfocusin={onfocusin} onfocusout={onfocusout}>
  {#each groups as g (g.group)}
    {#if FOLDED.has(g.group)}
      {#if g.branches.length}
        <details class="branch-group">
          <summary>{TITLES[g.group]} ({g.branches.length})</summary>
          <ul class="pr-list">
            {#each g.branches as b (b.name)}{@render row(b, g.group)}{/each}
          </ul>
        </details>
      {/if}
    {:else if g.branches.length || g.group === 'yours'}
      <section class="branch-group" aria-labelledby="branches-{g.group}">
        <h2 id="branches-{g.group}">{TITLES[g.group]}</h2>
        <ul class="pr-list">
          {#each g.branches as b (b.name)}{@render row(b, g.group)}{:else}<li class="muted empty-line">None of your branches is without a pull request.</li>{/each}
        </ul>
      </section>
    {/if}
  {/each}
  </div>
  {#if store.truncated}
    <p class="muted branches-note">Showing the {store.branches.length} branches GitHub lists first (by name) of {store.total}.</p>
  {/if}
{/if}
</LoadingGate>

<style>
  .compare-start {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
    align-items: center;
    margin: 0 0 16px;
    font-size: 13px;
  }
  .compare-start label {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .compare-start input {
    font: 12px ui-monospace, monospace;
    width: 14em;
    max-width: 100%;
  }
  .compare-start-error {
    flex-basis: 100%;
    margin: 0;
    color: var(--bad);
  }
  .branch-group {
    margin: 0 0 20px;
  }
  .branch-group h2,
  .branch-group summary {
    font-size: 13px;
    font-weight: 650;
    margin: 0 0 8px;
  }
  .branch-group summary {
    cursor: pointer;
  }
  .branch-row b.me {
    color: var(--accent);
  }
  .branches-note {
    font-size: 13px;
    margin: 0 0 16px;
  }
  .link-button {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: var(--bad);
    text-decoration: underline;
    cursor: pointer;
  }
</style>
